import { CommonModule } from "@angular/common";
import { HttpParams } from "@angular/common/http";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatTooltipModule } from "@angular/material/tooltip";
import { AltaRiesgoComponent } from "src/app/gestion-riesgos/alta-riesgo/alta-riesgo-wizard.component";
import { ConsultaHistorialCambiosComponent } from "src/app/proyectos/admin-proyectos/historial-de-cambios/consulta-historial-cambios/consulta-historial-cambios.component";
import { ModalSeguimientoRiesgoComponent } from "src/app/proyectos/admin-proyectos/modal-seguimiento-riesgo/modal-seguimiento-riesgo.component";
import { ProyectosService } from "src/app/proyectos/proyectos.service";
import { ConsultaList } from "src/app/shared/model/core/ConsultaList.model";
import { Area } from "src/app/shared/model/empleado/area.model";
import { Riesgos } from "src/app/shared/model/gestion-proyectos/riesgos/riesgo.model";
import { seguimientosRiesgos } from "src/app/shared/model/gestion-proyectos/seguimientoRiesgo";
import { Constantes } from "src/app/utilerias/constantes/constantes";

@Component({
  selector: "app-consulta-riesgo",
  templateUrl: "./consulta-riesgo-areas.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    AltaRiesgoComponent,
  ],
})
export class ConsultaRiesgoComponent implements OnInit {
  dialogRef: MatDialogRef<any>;
  consultaObject = new ConsultaList<Riesgos>();
  listRiesgos: Array<Riesgos> = [];
  totalRiesgos = 0;
  totalPages = 0;
  currentPage = 1;
  itemsPerPage = Constantes.CINCO;
  readonly pageSizeOptions = [5, 10, 20, 50];
  searchTerm = "";
  loading = false;
  mostrarMascara: boolean;
  areaConsultar: Area;
  @ViewChild("downloadReportesLink") private downloadLink: ElementRef;
  fileUrl;
  fechaActual: Date = new Date();
  mostrarPrincipal: any;
  riesgoSeleccionado: Riesgos;
  mostrarConsulta: boolean;
  esEditar: boolean;
  listaSeguimiento: Array<seguimientosRiesgos>;
  estatusPorRiesgo: { [id: number]: string } = {};

  constructor(private modalDialog: MatDialog, private service: ProyectosService) {}

  ngOnInit() {
    this.mostrarPrincipal = true;
    this.mostrarConsulta = false;
    this.mostrarMascara = false;
    this.consultaArea();
  }

  realizaPaginacion(): void {
    this.loading = true;
    this.consultaObject.regInicial = (this.currentPage - 1) * this.itemsPerPage;
    this.consultaObject.regXPagina = this.itemsPerPage;
    this.consultaObject.bean = new Riesgos();
    this.consultaObject.param1 = "area";
    this.consultaObject.sort = "id";
    this.consultaObject.param4 = Constantes.UNO;

    this.service.consultaRiesgoPaginacion(this.consultaObject).subscribe({
      next: (res) => {
        this.listRiesgos = [...(res?.list ?? [])];
        this.totalRiesgos = Number(res?.pTotalItems ?? this.listRiesgos.length);
        this.totalPages = Math.max(1, Math.ceil(this.totalRiesgos / this.itemsPerPage));

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages;
          this.realizaPaginacion();
          return;
        }

        this.listRiesgos.forEach((riesgo) => this.consultaUltSeguimiento(riesgo.id));
        this.loading = false;
      },
      error: () => {
        this.listRiesgos = [];
        this.totalRiesgos = 0;
        this.totalPages = 0;
        this.loading = false;
      },
    });
  }

  consultaArea(): void {
    this.service.consultaAreaxUsuario().subscribe((res) => {
      this.areaConsultar = res;
      this.realizaPaginacion();
    });
  }

  get filteredRiesgos(): Array<Riesgos> {
    const searchValue = this.searchTerm.trim().toLocaleLowerCase();
    if (!searchValue) {
      return this.listRiesgos;
    }

    return this.listRiesgos.filter((riesgo) =>
      [
        riesgo.id,
        riesgo.faseDeteccion?.nombre_faseDeteccion,
        riesgo.descripcion,
        riesgo.causa_raiz,
        riesgo.probabilidad?.nombre,
        riesgo.severidad?.descripcion,
        riesgo.estrategia?.estrategia,
        riesgo.periodicidad?.descripcion,
        riesgo.acciones_seguir,
        riesgo.responsable_recurso,
        this.estatusPorRiesgo[riesgo.id],
      ].some((value) => String(value ?? "").toLocaleLowerCase().includes(searchValue))
    );
  }

  onSearchChange(): void {
    // La tabla anterior recibía un bloque paginado del servicio; la búsqueda
    // conserva ese alcance y filtra los riesgos visibles de la página actual.
  }

  onItemsPerPageChange(value: number): void {
    this.itemsPerPage = Number(value);
    this.currentPage = 1;
    this.realizaPaginacion();
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.realizaPaginacion();
  }

  firstPage(): void { this.onPageChange(1); }
  previousPage(): void { this.onPageChange(this.currentPage - 1); }
  nextPage(): void { this.onPageChange(this.currentPage + 1); }
  lastPage(): void { this.onPageChange(this.totalPages); }

  getRangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalRiesgos);
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return this.totalPages === 1 ? [1] : [];
    }

    const pages = new Set<number>([1, this.totalPages, this.currentPage - 1, this.currentPage, this.currentPage + 1]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((left, right) => left - right);
    const items: Array<number | string> = [];

    sortedPages.forEach((page, index) => {
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    });
    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  openEditar(id: number): void {
    const riesgo = this.listRiesgos.find((item) => item.id === id);
    if (!riesgo) return;
    this.riesgoSeleccionado = riesgo;
    this.mostrarPrincipal = false;
    this.mostrarConsulta = true;
    this.esEditar = true;
  }

  agregarSeguimiento(riesgo: Riesgos | number): void {
    const riesgoData = typeof riesgo === "number" ? this.listRiesgos.find((item) => item.id === riesgo) : riesgo;
    if (!riesgoData) return;

    this.dialogRef = this.modalDialog.open(ModalSeguimientoRiesgoComponent, {
      disableClose: true,
      autoFocus: false,
      maxHeight: "calc(100vh - 32px)",
      maxWidth: "calc(100vw - 32px)",
      width: "1200px",
      panelClass: "app-standard-dialog",
      backdropClass: "app-standard-dialog-backdrop",
      ariaLabel: "Seguimientos de riesgo",
      data: { riesgo: riesgoData },
    });
  }

  mostrarMascaraConsulta(mostrar: boolean): void {
    this.mostrarMascara = mostrar;
  }

  mostrarVistaPrincipal(vista: boolean): void {
    if (vista) {
      this.mostrarPrincipal = true;
      this.mostrarConsulta = false;
      this.realizaPaginacion();
    }
  }

  descargarProyecto(): void {
    const anioActual = this.fechaActual.getFullYear();
    const httpParams = new HttpParams()
      .set("fecha", "hola")
      .set("inicio", "01/01/" + anioActual)
      .set("fin", "31/12/" + anioActual)
      .set("creditoId", "2")
      .set("idProyecto", "0")
      .set("idArea", this.areaConsultar.id);

    this.service.getReporteRiesgosExcel(httpParams).subscribe((response) => {
      if (response.type === "application/vnd.ms-excel") {
        this.fileUrl = window.URL.createObjectURL(new Blob([response], { type: "application/vnd.ms-excel" }));
        const link = this.downloadLink.nativeElement;
        link.href = this.fileUrl;
        link.download = "FOS-005_Plan_de_Respuesta_al_Riesgo";
        link.click();
        window.URL.revokeObjectURL(this.fileUrl);
      }
    });
  }

  openConsultaHistorial(riesgo: Riesgos | number): void {
    const riesgoData = typeof riesgo === "number" ? this.listRiesgos.find((item) => item.id === riesgo) : riesgo;
    if (!riesgoData) return;

    this.dialogRef = this.modalDialog.open(ConsultaHistorialCambiosComponent, {
      disableClose: true,
      autoFocus: false,
      width: "780px",
      maxHeight: "calc(100vh - 32px)",
      maxWidth: "calc(100vw - 32px)",
      panelClass: "app-standard-dialog",
      backdropClass: "app-standard-dialog-backdrop",
      ariaLabel: "Historial de cambios del riesgo",
      data: riesgoData,
    });
    this.dialogRef.afterClosed().subscribe();
  }

  consultaUltSeguimiento(idRiesgo: number): void {
    this.service.consultaUltSeguimiento(idRiesgo).subscribe((res) => {
      if (res?.list?.length > 0) {
        res.list.sort((left, right) => new Date(right.fecha).getTime() - new Date(left.fecha).getTime());
        this.estatusPorRiesgo[idRiesgo] = res.list[0].severidadDescripcion;
      } else {
        this.estatusPorRiesgo[idRiesgo] = "Sin seguimiento";
      }
    });
  }

  formatDate(value: string | Date): string {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "Sin fecha" : date.toLocaleDateString("es-MX");
  }
}
